import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLowStim } from "@/contexts/LowStimContext";
import { LogOut, Moon, Sun, Compass, ShieldCheck, ExternalLink, LayoutDashboard } from "lucide-react";
import { TID } from "@/lib/testIds";
import { ROLE_HOME } from "@/lib/sensory";
import { toast } from "sonner";

const IDLE_MS = 30 * 60 * 1000;    // 30 min hard limit for therapists
const WARN_MS = 28 * 60 * 1000;    // warn at 28 min

export default function Layout({ children }) {
  const { actor, logout } = useAuth();
  const { enabled: lowStim, toggle: toggleLowStim } = useLowStim();
  const nav = useNavigate();

  const home = actor && typeof actor === "object" ? (ROLE_HOME[actor.kind] || "/") : "/";
  const workspaceLabel = actor?.kind === "therapist" ? "My workspace" : actor?.kind === "student" ? "My app" : null;
  const year = new Date().getFullYear();

  // ---- Idle-session timeout (therapists only) ---------------------------
  React.useEffect(() => {
    if (!actor || actor.kind !== "therapist") return undefined;
    let warnTimer;
    let logoutTimer;
    let warned = false;
    const reset = () => {
      warned = false;
      clearTimeout(warnTimer);
      clearTimeout(logoutTimer);
      warnTimer = setTimeout(() => {
        warned = true;
        toast.warning("You'll be signed out in 2 minutes due to inactivity.", { duration: 6000 });
      }, WARN_MS);
      logoutTimer = setTimeout(async () => {
        await logout();
        toast.info("Signed out for security (inactivity).");
        nav("/login");
      }, IDLE_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    return () => {
      clearTimeout(warnTimer);
      clearTimeout(logoutTimer);
      events.forEach((e) => window.removeEventListener(e, reset));
      void warned;
    };
  }, [actor, logout, nav]);

  return (
    <div className="App min-h-screen bg-[var(--sw-bg)] text-[var(--sw-ink)] flex flex-col">
      <a href="#sw-main" className="sw-skip-link">Skip to main content</a>

      <header className="sticky top-0 z-40 backdrop-blur bg-[var(--sw-bg)]/85 border-b border-[var(--sw-line)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
          <Link to="/" data-testid={TID.navHome} className="flex items-center gap-2 min-w-0" aria-label="SenseWise home">
            <span className="inline-flex items-center justify-center h-9 w-9 rounded-xl bg-[var(--sw-primary)] text-[var(--sw-cream)] shrink-0" aria-hidden>
              <Compass size={18} />
            </span>
            <span className="display text-xl font-bold tracking-tight truncate">SenseWise</span>
            <span className="hidden md:inline ml-2 text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">by Occuthera</span>
          </Link>
          <div className="flex items-center gap-2 min-w-0">
            <button
              type="button"
              onClick={toggleLowStim}
              data-testid={TID.navLowStim}
              className="sw-pill h-11"
              data-active={lowStim}
              aria-pressed={lowStim}
              aria-label="Toggle low-stimulation mode"
              title="Low-stimulation mode"
            >
              {lowStim ? <Moon size={16} aria-hidden /> : <Sun size={16} aria-hidden />}
              <span className="hidden sm:inline">{lowStim ? "Low-stim ON" : "Low-stim"}</span>
            </button>
            {actor && typeof actor === "object" ? (
              <>
                <Link
                  to={home}
                  data-testid="nav-workspace-link"
                  className="sw-pill h-11 bg-[var(--sw-primary)] text-white border-[var(--sw-primary)]"
                  aria-label={workspaceLabel || "Workspace"}
                >
                  <LayoutDashboard size={16} aria-hidden />
                  <span className="hidden sm:inline">{workspaceLabel}</span>
                </Link>
                <span className="hidden lg:inline text-sm text-[var(--sw-ink-soft)] mr-2 truncate max-w-[220px]">
                  {actor.name}
                  {actor.organization ? <span className="text-[var(--sw-ink-soft)]"> · {actor.organization}</span> : null}
                  {" · "}<em className="not-italic">{actor.kind}</em>
                </span>
                <button
                  type="button"
                  data-testid={TID.logout}
                  onClick={async () => { await logout(); nav(actor.kind === "student" ? "/student-login" : "/login"); }}
                  className="sw-pill h-11"
                  aria-label="Sign out"
                >
                  <LogOut size={16} aria-hidden /> <span className="hidden sm:inline">Sign out</span>
                </button>
              </>
            ) : null}
          </div>
        </div>
        {/* Trust bar */}
        <div className="border-t border-[var(--sw-line)] bg-[var(--sw-bg-soft)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-8 flex items-center justify-center gap-4 text-[11px] font-semibold uppercase tracking-widest text-[var(--sw-ink-soft)] overflow-x-auto no-scrollbar">
            <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><ShieldCheck size={12} className="text-[var(--sw-primary)]" /> OT-built</span>
            <span aria-hidden className="opacity-40">·</span>
            <span className="whitespace-nowrap">Evidence-based</span>
            <span aria-hidden className="opacity-40">·</span>
            <span className="whitespace-nowrap">FERPA-aware</span>
            <span aria-hidden className="opacity-40">·</span>
            <span className="whitespace-nowrap">WCAG 2.1 AA target</span>
          </div>
        </div>
      </header>

      <main id="sw-main" tabIndex={-1} className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 py-6 md:py-10">
        {children}
      </main>

      <footer className="mt-12 border-t border-[var(--sw-line)] bg-[var(--sw-bg-soft)]" data-testid="site-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid gap-8 md:grid-cols-4 text-sm">
          <div>
            <div className="flex items-center gap-2">
              <span className="inline-flex items-center justify-center h-8 w-8 rounded-xl bg-[var(--sw-primary)] text-[var(--sw-cream)]" aria-hidden>
                <Compass size={16} />
              </span>
              <span className="display text-lg font-bold">SenseWise</span>
            </div>
            <p className="mt-3 text-[var(--sw-ink-soft)] leading-relaxed">
              A sensory self-regulation companion for OTs, educators, students, and school districts.
              Built on Ayres SI &amp; Dunn&apos;s Sensory Processing Model.
            </p>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">Product</div>
            <ul className="mt-3 space-y-2">
              <li><Link to="/explore" className="hover:underline">Try it now</Link></li>
              <li><Link to="/login" className="hover:underline">Therapist sign-in</Link></li>
              <li><Link to="/student-login" className="hover:underline">Student sign-in</Link></li>
              <li><Link to="/about" className="hover:underline">About</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">Clinical &amp; Legal</div>
            <ul className="mt-3 space-y-2">
              <li><Link to="/evidence" className="hover:underline">Evidence base</Link></li>
              <li><Link to="/accessibility" className="hover:underline">Accessibility statement</Link></li>
              <li><Link to="/privacy" className="hover:underline">Privacy policy</Link></li>
              <li><Link to="/terms" className="hover:underline">Terms of service</Link></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-[var(--sw-ink-soft)]">Occuthera</div>
            <ul className="mt-3 space-y-2 text-[var(--sw-ink-soft)]">
              <li>
                <a href="mailto:Hello@occuthera.com" className="hover:underline text-[var(--sw-ink)]">Hello@occuthera.com</a>
              </li>
              <li>
                <a href="https://occuthera.com" target="_blank" rel="noreferrer" className="hover:underline inline-flex items-center gap-1 text-[var(--sw-ink)]">
                  occuthera.com <ExternalLink size={12} aria-hidden />
                </a>
              </li>
              <li className="pt-2">
                <span className="inline-flex items-center gap-1.5 text-xs">
                  <ShieldCheck size={12} className="text-[var(--sw-primary)]" aria-hidden />
                  Request a district DPA at the email above.
                </span>
              </li>
            </ul>
          </div>
        </div>
        <div className="border-t border-[var(--sw-line)]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between gap-2 text-xs text-[var(--sw-ink-soft)]">
            <div>© {year} Occuthera. All rights reserved. SenseWise&trade; is a product of Occuthera.</div>
            <div className="italic">Not a medical device. Not a substitute for professional care. In a crisis, dial 988 (US).</div>
          </div>
        </div>
      </footer>
    </div>
  );
}
